/**
 * ProfileModule — Unified profile dashboard.
 *
 * Shows cross-app stats, achievements, activity feed,
 * account security, connected apps, and quick settings links.
 */

import type { AweniContext } from '../../context/AweniContext';

interface UnifiedProfile {
  user: { id: number; email: string; first_name: string; last_name: string; role: string; created_at: string };
  plan: { key: string; name: string };
  stats: {
    games_played: number;
    current_streak: number;
    cards_mastered: number;
    groups_count: number;
    total_active_days: number;
    longest_streak: number;
  };
  achievements: Array<{ key: string; name: string; earned: boolean }>;
  recent_activity: Array<{ action: string; app: string; timestamp: string; details?: string }>;
}

export default class ProfileModule {
  private _container: HTMLElement | null = null;
  private _ctx: AweniContext | null = null;

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this._container = container;
    this._ctx = context;

    if (!context.auth.isAuthenticated) {
      context.navigate('/login');
      return;
    }

    container.innerHTML = '';
    const t = context.i18n.t.bind(context.i18n);
    const dir = context.i18n.dir;

    // Wrapper
    const wrap = document.createElement('div');
    wrap.style.cssText = 'max-width:520px;margin:0 auto;padding:16px;';
    wrap.setAttribute('dir', dir);

    // Loading
    wrap.innerHTML = `<div style="text-align:center;padding:40px 0;"><div class="aweni-spinner"></div></div>`;
    container.appendChild(wrap);

    // Fetch unified profile
    let profile: UnifiedProfile | null = null;
    try {
      const token = context.auth.getToken();
      const [profileRes, activityRes] = await Promise.all([
        fetch('/api/profile/me', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch('/api/activity/feed?limit=10', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        }),
      ]);

      const profileData = profileRes.ok ? await profileRes.json() : null;
      const activityData = activityRes.ok ? await activityRes.json() : null;

      if (profileData) {
        profile = {
          user: {
            id: context.auth.user?.id || 0,
            email: context.auth.user?.email || '',
            first_name: context.auth.user?.name?.split(' ')[0] || '',
            last_name: context.auth.user?.name?.split(' ').slice(1).join(' ') || '',
            role: context.auth.user?.role || 'user',
            created_at: profileData.created_at || '',
          },
          plan: {
            key: profileData.plan_key || 'free',
            name: profileData.plan_key || 'free',
          },
          stats: {
            games_played: profileData.games?.total_played || 0,
            current_streak: profileData.streak?.current_streak || 0,
            cards_mastered: profileData.memoriz?.cards_mastered || 0,
            groups_count: profileData.balancetab?.groups_count || 0,
            total_active_days: profileData.streak?.total_active_days || 0,
            longest_streak: profileData.streak?.longest_streak || 0,
          },
          achievements: profileData.achievements || [],
          recent_activity: activityData?.events || activityData?.feed || [],
        };
      }
    } catch {
      // Continue with fallback data
    }

    wrap.innerHTML = '';

    if (!profile) {
      // Fallback — basic profile from JWT
      profile = {
        user: {
          id: context.auth.user?.id || 0,
          email: context.auth.user?.email || '',
          first_name: context.auth.user?.name || '',
          last_name: '',
          role: context.auth.user?.role || 'user',
          created_at: '',
        },
        plan: { key: 'free', name: 'Free' },
        stats: { games_played: 0, current_streak: 0, cards_mastered: 0, groups_count: 0, total_active_days: 0, longest_streak: 0 },
        achievements: [],
        recent_activity: [],
      };
    }

    // --- Render profile ---

    // Header card
    const header = document.createElement('div');
    header.style.cssText = 'background:var(--ln-bg-card,#FFFFFF);border-radius:20px;padding:24px;text-align:center;margin-bottom:16px;border:1px solid var(--ln-border,#E8E1D2);';

    const initials = (profile.user.first_name[0] || '').toUpperCase();
    const avatarUrl = context.auth.user?.avatar_url;
    const avatarHtml = avatarUrl
      ? `<img src="${avatarUrl}" alt="" style="width:72px;height:72px;border-radius:50%;object-fit:cover;border:3px solid #0A0A0A;">`
      : `<div style="width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#0A0A0A,#3B82F6);display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:700;color:#fff;margin:0 auto;border:3px solid rgba(10, 10, 10,0.3);">${initials}</div>`;

    const planKey = profile.plan.key;
    const planLabel = t(`profile_plan_${planKey}`) !== `profile_plan_${planKey}` ? t(`profile_plan_${planKey}`) : planKey;

    header.innerHTML = `
      ${avatarHtml}
      <h1 style="font-size:1.3rem;font-weight:700;color:var(--ln-text-primary,#0A0A0A);margin:12px 0 2px;">${_esc(profile.user.first_name)} ${_esc(profile.user.last_name)}</h1>
      <p style="font-size:0.85rem;color:var(--ln-text-secondary,#5A5147);margin:0 0 8px;">${_esc(profile.user.email)}</p>
      <span style="display:inline-block;padding:4px 12px;border-radius:12px;background:rgba(10, 10, 10,0.12);color:#0A0A0A;font-size:0.75rem;font-weight:600;">${_esc(planLabel)}</span>
    `;
    wrap.appendChild(header);

    // Stats grid
    const statsGrid = document.createElement('div');
    statsGrid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;';

    const statItems = [
      { label: t('profile_stats_games'), value: profile.stats.games_played, color: '#3B82F6' },
      { label: t('profile_stats_streak'), value: profile.stats.current_streak, color: '#F59E0B' },
      { label: t('profile_stats_cards'), value: profile.stats.cards_mastered, color: '#8B5CF6' },
      { label: t('profile_stats_groups'), value: profile.stats.groups_count, color: '#0A0A0A' },
    ];

    for (const stat of statItems) {
      const card = document.createElement('div');
      card.style.cssText = `background:var(--ln-bg-card,#FFFFFF);border-radius:14px;padding:16px;text-align:center;border:1px solid var(--ln-border,#E8E1D2);`;
      card.innerHTML = `
        <div style="font-size:1.5rem;font-weight:800;color:${stat.color};">${stat.value}</div>
        <div style="font-size:0.75rem;color:var(--ln-text-secondary,#5A5147);margin-top:4px;">${stat.label}</div>
      `;
      statsGrid.appendChild(card);
    }
    wrap.appendChild(statsGrid);

    // Achievements
    if (profile.achievements.length > 0) {
      const achSection = _createSection(t('profile_achievements'));
      const achGrid = document.createElement('div');
      achGrid.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';
      for (const ach of profile.achievements) {
        const badge = document.createElement('span');
        badge.style.cssText = `padding:6px 12px;border-radius:20px;font-size:0.75rem;font-weight:600;${
          ach.earned
            ? 'background:rgba(10, 10, 10,0.12);color:#0A0A0A;'
            : 'background:rgba(100,116,139,0.1);color:#64748B;'
        }`;
        badge.textContent = ach.name || ach.key;
        achGrid.appendChild(badge);
      }
      achSection.appendChild(achGrid);
      wrap.appendChild(achSection);
    }

    // Pro tools — conditional, visible only if user has a SelectO professional role.
    // External link to /selectow/ (Stripe pattern: pro dashboard at its own URL, opened in new tab).
    const selectoRole = context.auth.user?.app_roles?.selecto;
    const isVenuePro = !!selectoRole && ['owner', 'manager', 'staff', 'app_admin'].includes(selectoRole);
    if (isVenuePro) {
      const proSection = _createSection(t('profile_pro_tools'));
      const proRow = document.createElement('a');
      proRow.href = 'https://learnature-ly.com/selectow/';
      proRow.target = '_blank';
      proRow.rel = 'noopener noreferrer';
      proRow.style.cssText = 'display:flex;align-items:center;gap:12px;width:100%;padding:14px 16px;background:var(--ln-bg-card,#FFFFFF);border:1px solid var(--ln-border,#E8E1D2);border-radius:14px;cursor:pointer;color:var(--ln-text-primary,#0A0A0A);font-size:0.9rem;font-weight:500;text-align:start;text-decoration:none;';
      const externalIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ln-text-secondary,#5A5147)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
      proRow.innerHTML = `
        <span style="width:36px;height:36px;border-radius:10px;background:rgba(156,131,72,0.12);display:flex;align-items:center;justify-content:center;flex-shrink:0;">${_storeIcon()}</span>
        <span style="flex:1;display:flex;flex-direction:column;gap:2px;min-width:0;">
          <span>${t('profile_pro_tools_my_venue')}</span>
          <span style="font-size:0.7rem;color:var(--ln-text-secondary,#5A5147);font-weight:400;">${t('profile_pro_tools_external_hint')}</span>
        </span>
        ${externalIcon}
      `;
      proSection.appendChild(proRow);
      wrap.appendChild(proSection);
    }

    // Quick actions
    const actionsSection = _createSection('');
    const actions = [
      { label: t('profile_edit'), icon: _userIcon(), route: '/settings' },
      { label: t('profile_security'), icon: _shieldIcon(), route: '/settings' },
      { label: t('profile_manage_family'), icon: _familyIcon(), route: '/family' },
      { label: t('profile_upgrade'), icon: _starIcon(), route: '/more/upgrade' },
    ];

    for (const action of actions) {
      const row = document.createElement('button');
      row.style.cssText = 'display:flex;align-items:center;gap:12px;width:100%;padding:14px 16px;background:var(--ln-bg-card,#FFFFFF);border:1px solid var(--ln-border,#E8E1D2);border-radius:14px;margin-bottom:8px;cursor:pointer;color:var(--ln-text-primary,#0A0A0A);font-size:0.9rem;font-weight:500;text-align:start;';
      row.innerHTML = `
        <span style="width:36px;height:36px;border-radius:10px;background:rgba(10, 10, 10,0.08);display:flex;align-items:center;justify-content:center;flex-shrink:0;">${action.icon}</span>
        <span style="flex:1;">${action.label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ln-text-secondary,#5A5147)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="${dir === 'rtl' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}"/></svg>
      `;
      row.addEventListener('click', () => context.navigate(action.route));
      actionsSection.appendChild(row);
    }
    wrap.appendChild(actionsSection);

    // Recent activity
    const actSection = _createSection(t('profile_recent_activity'));
    if (profile.recent_activity.length === 0) {
      const empty = document.createElement('p');
      empty.style.cssText = 'font-size:0.85rem;color:var(--ln-text-secondary,#5A5147);text-align:center;padding:16px 0;';
      empty.textContent = t('profile_no_activity');
      actSection.appendChild(empty);
    } else {
      for (const event of profile.recent_activity.slice(0, 8)) {
        const item = document.createElement('div');
        item.style.cssText = 'display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--ln-border,#E8E1D2);';
        const appColors: Record<string, string> = {
          games: '#3B82F6', memoriz: '#F59E0B', balancetab: '#8B5CF6',
          selecto: '#F97316', blog: '#14B8A6', aweni: '#0A0A0A',
        };
        const color = appColors[event.app] || '#6B7280';
        item.innerHTML = `
          <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:0.85rem;color:var(--ln-text-primary,#0A0A0A);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${_esc(event.action)}</div>
            <div style="font-size:0.7rem;color:var(--ln-text-secondary,#5A5147);">${_esc(event.app)} · ${_timeAgo(event.timestamp)}</div>
          </div>
        `;
        actSection.appendChild(item);
      }
    }
    wrap.appendChild(actSection);

    container.appendChild(wrap);
  }

  unmount(): void {
    if (this._container) this._container.innerHTML = '';
  }
}

function _createSection(title: string): HTMLElement {
  const section = document.createElement('div');
  section.style.cssText = 'margin-bottom:16px;';
  if (title) {
    const h = document.createElement('h2');
    h.style.cssText = 'font-size:0.95rem;font-weight:700;color:var(--ln-text-primary,#0A0A0A);margin:0 0 12px;';
    h.textContent = title;
    section.appendChild(h);
  }
  return section;
}

function _esc(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function _timeAgo(timestamp: string): string {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function _userIcon(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
}
function _shieldIcon(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
}
function _familyIcon(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>';
}
function _starIcon(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
}
function _storeIcon(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9C8348" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5"/><path d="M5 9v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9"/><path d="M9 21V12h6v9"/></svg>';
}
